import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Box, Typography, Switch } from '@mui/material';
import { useFieldEdit } from '../../../../contexts/FieldEditContext';
import TextEditor from '../../TextEditor';

/**
 * HeaderSettings
 * -----------------------------------------------------------------------------
 * Enterprise-grade header field settings editor.
 *
 * GOALS
 * - Header/subheader typing should behave like a local editor.
 * - Do NOT persist every keystroke into FieldEditContext / FieldMetadataContext.
 * - Do NOT send full metadata snapshots.
 * - Keep local draft state while typing.
 * - Commit small, scoped patches only:
 *     - on editor blur
 *     - on outside click, before "Done" can save fieldChanges
 *     - on unmount safety
 *     - immediately for simple toggle changes
 *
 * WHY THIS MATTERS
 * The previous implementation debounced updateField(...) while typing and passed:
 *   { ...selectedFieldForEdit.metadata, ...updatedValues }
 *
 * That caused:
 *   HeaderSettings typing
 *   → updateField
 *   → selectedFieldForEdit changes
 *   → FormLayout pushes selectedFieldForEdit into FieldMetadataContext
 *   → builder canvas re-renders
 *   → HeaderSettings receives selectedFieldForEdit again
 *
 * This version treats the sidebar editor as the draft source of truth during editing.
 */

const DEFAULT_HEADER_STYLE = {
    fontSize: '24px',
    headerColor: '#000000',
    textAlign: 'center',
    fontWeight: 'normal',
    fontStyle: 'normal',
    textDecoration: 'none',
    fontFamily: 'Arial',
    lineHeight: 1.25,
    margin: '0px 0',
    padding: '0px 0',
};

const DEFAULT_SUBHEADER_STYLE = {
    subFontSize: '16px',
    subheaderColor: '#666666',
    subheaderFontWeight: 'normal',
    subheaderFontStyle: 'normal',
    subheaderTextDecoration: 'none',
    subheaderFontFamily: 'Arial',
    textAlign: 'center',
    lineHeight: 1.35,
    margin: '0px 0',
    padding: '0px 0',
};

const shallowEqual = (obj1, obj2) => {
    if (obj1 === obj2) return true;
    if (!obj1 || !obj2) return false;

    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) return false;

    return keys1.every((key) => obj1[key] === obj2[key]);
};

/**
 * HeaderSettings consumes intent metadata from RichTextEditorBase.
 *
 * ENTERPRISE RULE:
 * Do not infer user intent from generic editor style values.
 * A style should update persisted header/subheader metadata only when the
 * editor explicitly says the toolbar changed a root/base style.
 *
 * Example:
 * - Typing / blur / Done should preserve subheaderStyle.subheaderColor.
 * - Selecting text and changing color should be represented by inline editor HTML.
 * - Changing alignment should update the root/base textAlign metadata.
 */
const shouldApplyRootEditorStyle = (editorIntent = {}, styleKey) => {
    return (
        editorIntent.changeSource === 'toolbar' &&
        editorIntent.affectsRootStyle === true &&
        editorIntent.changedStyleKey === styleKey
    );
};

const buildInitialLocalValues = (metadata = {}) => {
    return {
        header: metadata.header || '',
        enableSubheader:
            metadata.enableSubheader !== undefined
                ? metadata.enableSubheader
                : false,
        subheader: metadata.subheader || '',
        headerStyle: {
            ...DEFAULT_HEADER_STYLE,
            ...(metadata.headerStyle || {}),
            // Preserve existing schema defaults explicitly
            fontSize: metadata.headerStyle?.fontSize || DEFAULT_HEADER_STYLE.fontSize,
            headerColor: metadata.headerStyle?.headerColor || DEFAULT_HEADER_STYLE.headerColor,
            textAlign: metadata.headerStyle?.textAlign || DEFAULT_HEADER_STYLE.textAlign,
            fontWeight: metadata.headerStyle?.fontWeight || DEFAULT_HEADER_STYLE.fontWeight,
            fontStyle: metadata.headerStyle?.fontStyle || DEFAULT_HEADER_STYLE.fontStyle,
            textDecoration: metadata.headerStyle?.textDecoration || DEFAULT_HEADER_STYLE.textDecoration,
            fontFamily: metadata.headerStyle?.fontFamily || DEFAULT_HEADER_STYLE.fontFamily,
            lineHeight: metadata.headerStyle?.lineHeight || DEFAULT_HEADER_STYLE.lineHeight,
            margin: metadata.headerStyle?.margin || DEFAULT_HEADER_STYLE.margin,
            padding: metadata.headerStyle?.padding || DEFAULT_HEADER_STYLE.padding,
        },
        subheaderStyle: {
            ...DEFAULT_SUBHEADER_STYLE,
            ...(metadata.subheaderStyle || {}),
            // Preserve existing schema defaults explicitly
            subFontSize: metadata.subheaderStyle?.subFontSize || DEFAULT_SUBHEADER_STYLE.subFontSize,
            subheaderColor:
                metadata.subheaderStyle?.subheaderColor || DEFAULT_SUBHEADER_STYLE.subheaderColor,
            subheaderFontWeight:
                metadata.subheaderStyle?.subheaderFontWeight ||
                DEFAULT_SUBHEADER_STYLE.subheaderFontWeight,
            subheaderFontStyle:
                metadata.subheaderStyle?.subheaderFontStyle ||
                DEFAULT_SUBHEADER_STYLE.subheaderFontStyle,
            subheaderTextDecoration:
                metadata.subheaderStyle?.subheaderTextDecoration ||
                DEFAULT_SUBHEADER_STYLE.subheaderTextDecoration,
            subheaderFontFamily:
                metadata.subheaderStyle?.subheaderFontFamily ||
                DEFAULT_SUBHEADER_STYLE.subheaderFontFamily,
            textAlign: metadata.subheaderStyle?.textAlign || DEFAULT_SUBHEADER_STYLE.textAlign,
            lineHeight: metadata.subheaderStyle?.lineHeight || DEFAULT_SUBHEADER_STYLE.lineHeight,
            margin: metadata.subheaderStyle?.margin || DEFAULT_SUBHEADER_STYLE.margin,
            padding: metadata.subheaderStyle?.padding || DEFAULT_SUBHEADER_STYLE.padding,
        },
    };
};

const normalizeHeaderEditorStyles = (styles = {}, previousStyle = {}, editorIntent = {}) => {
    const nextStyle = {
        ...DEFAULT_HEADER_STYLE,
        ...previousStyle,

        // Preserve existing root metadata by default.
        // Inline toolbar formatting is stored in the editor HTML, not here.
        fontSize: previousStyle.fontSize || DEFAULT_HEADER_STYLE.fontSize,
        headerColor: previousStyle.headerColor || DEFAULT_HEADER_STYLE.headerColor,
        textAlign: previousStyle.textAlign || DEFAULT_HEADER_STYLE.textAlign,
        fontWeight: previousStyle.fontWeight || DEFAULT_HEADER_STYLE.fontWeight,
        fontStyle: previousStyle.fontStyle || DEFAULT_HEADER_STYLE.fontStyle,
        textDecoration: previousStyle.textDecoration || DEFAULT_HEADER_STYLE.textDecoration,
        fontFamily: previousStyle.fontFamily || DEFAULT_HEADER_STYLE.fontFamily,
        lineHeight: previousStyle.lineHeight || DEFAULT_HEADER_STYLE.lineHeight,

        // Preserve layout spacing controls
        margin: previousStyle.margin || DEFAULT_HEADER_STYLE.margin,
        padding: previousStyle.padding || DEFAULT_HEADER_STYLE.padding,
    };

    if (shouldApplyRootEditorStyle(editorIntent, 'textAlign')) {
        nextStyle.textAlign =
            styles.textAlign ||
            editorIntent.changedStyleValue ||
            previousStyle.textAlign ||
            DEFAULT_HEADER_STYLE.textAlign;
    }

    return nextStyle;
};

const normalizeSubheaderEditorStyles = (styles = {}, previousStyle = {}, editorIntent = {}) => {
    const nextStyle = {
        ...DEFAULT_SUBHEADER_STYLE,
        ...previousStyle,

        // Preserve existing root metadata by default.
        // Inline toolbar formatting is stored in the editor HTML, not here.
        subFontSize: previousStyle.subFontSize || DEFAULT_SUBHEADER_STYLE.subFontSize,
        subheaderColor: previousStyle.subheaderColor || DEFAULT_SUBHEADER_STYLE.subheaderColor,
        subheaderFontWeight:
            previousStyle.subheaderFontWeight || DEFAULT_SUBHEADER_STYLE.subheaderFontWeight,
        subheaderFontStyle:
            previousStyle.subheaderFontStyle || DEFAULT_SUBHEADER_STYLE.subheaderFontStyle,
        subheaderTextDecoration:
            previousStyle.subheaderTextDecoration || DEFAULT_SUBHEADER_STYLE.subheaderTextDecoration,
        subheaderFontFamily:
            previousStyle.subheaderFontFamily || DEFAULT_SUBHEADER_STYLE.subheaderFontFamily,
        textAlign: previousStyle.textAlign || DEFAULT_SUBHEADER_STYLE.textAlign,
        lineHeight: previousStyle.lineHeight || DEFAULT_SUBHEADER_STYLE.lineHeight,

        // Preserve layout spacing controls
        margin: previousStyle.margin || DEFAULT_SUBHEADER_STYLE.margin,
        padding: previousStyle.padding || DEFAULT_SUBHEADER_STYLE.padding,
    };

    if (shouldApplyRootEditorStyle(editorIntent, 'textAlign')) {
        nextStyle.textAlign =
            styles.textAlign ||
            editorIntent.changedStyleValue ||
            previousStyle.textAlign ||
            DEFAULT_SUBHEADER_STYLE.textAlign;
    }

    return nextStyle;
};

const buildPatchFromDraft = (draftValues = {}, lastCommittedValues = {}) => {
    const patch = {};

    if (draftValues.header !== lastCommittedValues.header) {
        patch.header = draftValues.header;
    }

    if (draftValues.enableSubheader !== lastCommittedValues.enableSubheader) {
        patch.enableSubheader = draftValues.enableSubheader;
    }

    if (draftValues.subheader !== lastCommittedValues.subheader) {
        patch.subheader = draftValues.subheader;
    }

    if (!shallowEqual(draftValues.headerStyle, lastCommittedValues.headerStyle)) {
        patch.headerStyle = draftValues.headerStyle;
    }

    if (!shallowEqual(draftValues.subheaderStyle, lastCommittedValues.subheaderStyle)) {
        patch.subheaderStyle = draftValues.subheaderStyle;
    }

    return patch;
};

const HeaderSettings = () => {
    const { selectedFieldForEdit, updateField } = useFieldEdit();

    console.log('[HeaderSettings: debug] Received selectedFieldForEdit:', selectedFieldForEdit);

    const selectedFieldId = selectedFieldForEdit?.id || null;

    const containerRef = useRef(null);
    const latestLocalValuesRef = useRef(buildInitialLocalValues(selectedFieldForEdit?.metadata || {}));
    const lastCommittedValuesRef = useRef(buildInitialLocalValues(selectedFieldForEdit?.metadata || {}));
    const dirtyRef = useRef(false);
    const selectedFieldIdRef = useRef(selectedFieldId);

    const [localValues, setLocalValues] = useState(() => {
        const initialValues = buildInitialLocalValues(selectedFieldForEdit?.metadata || {});
        latestLocalValuesRef.current = initialValues;
        lastCommittedValuesRef.current = initialValues;

        return initialValues;
    });

    /**
     * Initialize local draft state only when switching to a different field.
     *
     * IMPORTANT:
     * Do not depend on the full selectedFieldForEdit object here. FieldEditContext
     * updates selectedFieldForEdit.metadata as patches arrive. Rehydrating local
     * editor state on every metadata object change is what causes cursor/selection
     * instability for contentEditable editors.
     */
    useEffect(() => {
        if (!selectedFieldForEdit?.id || !selectedFieldForEdit?.metadata) return;

        const initialValues = buildInitialLocalValues(selectedFieldForEdit.metadata);

        selectedFieldIdRef.current = selectedFieldForEdit.id;
        latestLocalValuesRef.current = initialValues;
        lastCommittedValuesRef.current = initialValues;
        dirtyRef.current = false;

        setLocalValues(initialValues);

        console.log('[HeaderSettings:init] Initialized local header draft for field:', {
            fieldId: selectedFieldForEdit.id,
            initialValues,
        });
    }, [selectedFieldForEdit?.id]);

    const commitDraftToContext = useCallback(
        (reason = 'manual') => {
            const fieldId = selectedFieldIdRef.current;
            if (!fieldId) return;

            const draftValues = latestLocalValuesRef.current;
            const lastCommittedValues = lastCommittedValuesRef.current;

            const patch = buildPatchFromDraft(draftValues, lastCommittedValues);

            if (!dirtyRef.current && Object.keys(patch).length === 0) {
                return;
            }

            if (Object.keys(patch).length === 0) {
                dirtyRef.current = false;
                return;
            }

            console.log('[HeaderSettings:commitDraftToContext] Committing header patch:', {
                reason,
                fieldId,
                patchKeys: Object.keys(patch),
                patch,
            });

            updateField(fieldId, patch);

            lastCommittedValuesRef.current = {
                ...lastCommittedValues,
                ...patch,
                headerStyle: patch.headerStyle || lastCommittedValues.headerStyle,
                subheaderStyle: patch.subheaderStyle || lastCommittedValues.subheaderStyle,
            };

            dirtyRef.current = false;
        },
        [updateField]
    );

    /**
     * Commit before outside actions such as clicking Done.
     *
     * The Done button lives outside this component. Capturing mousedown lets this
     * component flush its local draft into FieldEditContext before FieldSettings
     * calls saveFieldChanges().
     *
     * Ignore MUI portal menus/popovers so selecting toolbar dropdown options does
     * not prematurely commit root metadata.
     */
    useEffect(() => {
        const handleDocumentMouseDown = (event) => {
            const target = event.target;

            if (!target) return;

            if (containerRef.current && containerRef.current.contains(target)) {
                return;
            }

            if (
                target.closest &&
                (
                    target.closest('.MuiPopover-root') ||
                    target.closest('.MuiMenu-root') ||
                    target.closest('[role="listbox"]')
                )
            ) {
                return;
            }

            commitDraftToContext('outside-click');
        };

        document.addEventListener('mousedown', handleDocumentMouseDown, true);

        return () => {
            document.removeEventListener('mousedown', handleDocumentMouseDown, true);
        };
    }, [commitDraftToContext]);

    /**
     * Unmount safety.
     *
     * This is not the primary Done-path flush because FieldSettings may call
     * saveFieldChanges before this component unmounts. The outside-click capture
     * above handles that primary path. This is only a final guard.
     */
    useEffect(() => {
        return () => {
            commitDraftToContext('unmount');
        };
    }, [commitDraftToContext]);

    const handleChange = useCallback(
        (
            {
                text,
                styles,
                changeSource,
                changedStyleKey,
                changedStyleValue,
                affectsRootStyle,
            },
            isSubheader = false
        ) => {
            if (!selectedFieldId) return;

            const editorIntent = {
                changeSource,
                changedStyleKey,
                changedStyleValue,
                affectsRootStyle,
            };

            setLocalValues((prevValues) => {
                const updatedValues = isSubheader
                    ? {
                          ...prevValues,
                          subheader: text,
                          subheaderStyle: normalizeSubheaderEditorStyles(
                              styles,
                              prevValues.subheaderStyle,
                              editorIntent
                          ),
                      }
                    : {
                          ...prevValues,
                          header: text,
                          headerStyle: normalizeHeaderEditorStyles(
                              styles,
                              prevValues.headerStyle,
                              editorIntent
                          ),
                      };

                latestLocalValuesRef.current = updatedValues;
                dirtyRef.current = true;

                console.log('[HeaderSettings:draft] Updated local header draft:', {
                    fieldId: selectedFieldId,
                    target: isSubheader ? 'subheader' : 'header',
                    textLength: typeof text === 'string' ? text.length : 0,
                    styleKeys: styles ? Object.keys(styles) : [],
                    changeSource,
                    changedStyleKey,
                    changedStyleValue,
                    affectsRootStyle,
                });

                return updatedValues;
            });
        },
        [selectedFieldId]
    );

    const handleHeaderBlur = useCallback(() => {
        commitDraftToContext('header-blur');
    }, [commitDraftToContext]);

    const handleSubheaderBlur = useCallback(() => {
        commitDraftToContext('subheader-blur');
    }, [commitDraftToContext]);

    const handleSubheaderToggle = useCallback(
        (event) => {
            if (!selectedFieldId) return;

            const isEnabled = event.target.checked;

            setLocalValues((prevValues) => {
                const updatedValues = {
                    ...prevValues,
                    enableSubheader: isEnabled,
                };

                latestLocalValuesRef.current = updatedValues;
                dirtyRef.current = true;

                return updatedValues;
            });

            console.log('[HeaderSettings:handleSubheaderToggle] Updating enableSubheader:', {
                fieldId: selectedFieldId,
                enableSubheader: isEnabled,
            });

            // Toggle is a small scalar setting. Commit it immediately so the UI state
            // and edit buffer stay aligned without sending the whole metadata object.
            updateField(selectedFieldId, { enableSubheader: isEnabled });

            lastCommittedValuesRef.current = {
                ...lastCommittedValuesRef.current,
                enableSubheader: isEnabled,
            };
        },
        [selectedFieldId, updateField]
    );

    if (!selectedFieldForEdit || !selectedFieldForEdit.metadata) {
        return <Typography>Select a field to edit</Typography>;
    }

    return (
        <Box ref={containerRef} sx={{ maxWidth: '90%', mx: 'auto' }}>
            <Typography variant="body2" sx={{ ml: 1, mb: 1.5, mt: 1 }}>
                <span style={{ fontWeight: 'bold', color: '#003459' }}>Field Type:</span>
                <span style={{ fontWeight: 'light', color: 'text.secondary', marginLeft: '8px' }}>
                    {selectedFieldForEdit.metadata.type || 'header'}
                </span>
            </Typography>

            {/* Header Text Editor */}
            <TextEditor
                value={localValues.header}
                onChange={(data) => handleChange(data, false)}
                onBlur={handleHeaderBlur}
                placeholder="Enter the main header text..."
                styles={localValues.headerStyle}
                toolbarStyles={{ mb: 0 }}
            />

            {/* Subheader Toggle */}
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                <Typography sx={{ mr: 2 }}>Enable Subheader</Typography>
                <Switch
                    checked={localValues.enableSubheader}
                    onChange={handleSubheaderToggle}
                    name="enableSubheader"
                />
            </Box>

            {/* Subheader Text Editor */}
            {localValues.enableSubheader && (
                <TextEditor
                    value={localValues.subheader}
                    onChange={(data) => handleChange(data, true)}
                    onBlur={handleSubheaderBlur}
                    placeholder="Enter the subheader text..."
                    styles={localValues.subheaderStyle}
                    toolbarStyles={{ mb: 0 }}
                />
            )}
        </Box>
    );
};

export default HeaderSettings;